"use client";

import { useState } from "react";
import { Table2 } from "lucide-react";
import { Card, CardContent } from "@/app/_module/components/ui/card";
import { Checkbox } from "@/app/_module/components/ui/checkbox";
import { Button } from "@/app/_module/components/ui/button";
import { useToast } from "@/app/_module/hooks/use-toast";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { LEAD_TABLE_COLUMNS, DEFAULT_LEAD_COLUMNS } from "@/app/(authenticated)/leads/_modules/components/LeadsSearch";
import { CustomFieldColumnPicker, isCfColumn } from "@/app/(authenticated)/leads/_modules/components/CustomFieldColumnPicker";
import { useCustomFieldKeys } from "@/app/(authenticated)/leads/_modules/hooks/queries";

/**
 * Per-user table layout. Same store the Leads page's Filters → Columns writes
 * (appPreferences.tableColumns.leads), so the two never disagree.
 */
export function TableSettings() {
  const { user, refetchUser } = useUserContext();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const { data: customFieldKeys = [] } = useCustomFieldKeys();
  const tableColumns = ((user as any)?.user?.appPreferences?.tableColumns || {}) as Record<string, string[]>;
  const leadsColumns: string[] = Array.isArray(tableColumns.leads) ? tableColumns.leads : DEFAULT_LEAD_COLUMNS;

  const save = async (leads: string[]) => {
    const next = { ...tableColumns, leads };
    refetchUser((prev: any) => ({
      ...prev,
      user: { ...prev.user, appPreferences: { ...(prev.user?.appPreferences || {}), tableColumns: next } },
    }));
    setSaving(true);
    try {
      // PATCH shallow-merges top-level keys: always send the whole tableColumns object.
      await api.patch({ url: getApiUrl("/users/profile/app-preferences"), body: { tableColumns: next }, auth: true });
    } catch {
      toast({ title: "Not saved", description: "Column choice could not be saved. Try again.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const toggle = (key: string, on: boolean) =>
    save(on ? [...leadsColumns.filter((k) => k !== key), key] : leadsColumns.filter((k) => k !== key));

  return (
    <Card className="border shadow-sm">
      <CardContent className="pt-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
            <Table2 className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Leads table</h3>
            <p className="text-sm text-muted-foreground">
              Columns shown on the Leads page. Saved to your account, so they follow you to every tab and device.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3">
          {LEAD_TABLE_COLUMNS.map((col) => (
            <label key={col.key} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-muted/50">
              <Checkbox
                checked={leadsColumns.includes(col.key)}
                disabled={saving}
                onCheckedChange={(checked) => toggle(col.key, checked === true)}
              />
              <span className="text-sm">{col.label}</span>
            </label>
          ))}
        </div>

        <div className="mt-4 border-t pt-4">
          <CustomFieldColumnPicker fields={customFieldKeys} selected={leadsColumns} onToggle={toggle} disabled={saving} />
        </div>

        <div className="mt-5 flex items-center justify-between border-t pt-4">
          <p className="text-xs text-muted-foreground">
            {leadsColumns.filter((k) => !isCfColumn(k)).length} of {LEAD_TABLE_COLUMNS.length} columns shown
            {leadsColumns.some(isCfColumn) ? ` · ${leadsColumns.filter(isCfColumn).length} custom` : ""}
          </p>
          <Button variant="outline" size="sm" disabled={saving} onClick={() => save(DEFAULT_LEAD_COLUMNS)}>
            Reset to default
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
